import { Injectable } from '@angular/core';
import { ProductVariant } from '../interfaces/wheels';

export interface CartItem {
  product_id: string | number;
  product_variant_id: string | number;
  type?: 'front' | 'rear';
  quantity: number;
  custom_finish?: string;
  custom_finish_price?: string;
  bolt_pattern?: string;
  offset?: string;
  cost_per_wheel?: string;
  vehicle_year_make_model?: string;
}

export interface CartValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface StaggeredPricing {
  frontPrice: number;
  rearPrice: number;
  totalPrice: number;
}

@Injectable({
  providedIn: 'root'
})
export class WheelCartService {

  /**
   * Validates cart items for proper structure and required fields
   */
  validateCartItems(items: CartItem[]): CartValidationResult {
    const errors: string[] = [];
    
    if (!items || items.length === 0) {
      errors.push("No items to add to cart");
      return { isValid: false, errors };
    }
    
    items.forEach((item, index) => {
      if (!item.product_id) {
        errors.push(`Item ${index + 1}: Missing product ID`);
      }
      if (!item.product_variant_id) {
        errors.push(`Item ${index + 1}: Missing product variant ID`);
      }
      if (!item.quantity || item.quantity <= 0) {
        errors.push(`Item ${index + 1}: Invalid quantity`);
      }
      if (item.type && !['front', 'rear'].includes(item.type)) {
        errors.push(`Item ${index + 1}: Invalid wheel type`);
      }
    });
    
    return { isValid: errors.length === 0, errors };
  }

  /**
   * Builds cart items for staggered wheel setup (search-by-size)
   */
  buildStaggeredCartItems(
    product: ProductVariant, 
    frontWheelId: string | number, 
    rearWheelId: string | number, 
    frontQty: number, 
    rearQty: number
  ): CartItem[] {
    const items: CartItem[] = [];
    
    if (frontWheelId) {
      items.push({
        product_id: product.product_id || product.id,
        product_variant_id: frontWheelId,
        type: 'front',
        quantity: frontQty
      });
    }
    
    if (rearWheelId) {
      items.push({
        product_id: product.product_id || product.id,
        product_variant_id: rearWheelId,
        type: 'rear',
        quantity: rearQty // Fixed: was using frontQty for rear wheels
      });
    }
    
    return items;
  }

  /**
   * Builds cart items for regular wheel setup (vehicle-based search)
   */
  buildRegularCartItems(
    product: ProductVariant,
    wheelId: string | number,
    quantity: number
  ): CartItem[] {
    return [{
      product_id: product.product_id || product.id,
      product_variant_id: wheelId,
      quantity: quantity
    }];
  }

  /**
   * Gets total quantity from cart items
   */
  getTotalQuantity(items: CartItem[]): number {
    if (!items || items.length === 0) {
      return 0;
    }
    
    return items.reduce((total, item) => {
      return total + (item.quantity || 0);
    }, 0);
  }

  /**
   * Checks if cart items are valid for adding to cart
   */
  canAddToCart(items: CartItem[]): boolean {
    if (!items || items.length === 0) {
      return false;
    }
    
    return items.every(item => 
      item.quantity && item.quantity > 0 && 
      item.product_variant_id && 
      item.product_id
    );
  }

  /**
   * Prepares cart data for API submission
   */
  prepareCartData(items: CartItem[], sessionId: string): any {
    return {
      session_id: sessionId,
      items: items
    };
  }
}
